/**
 * @see https://v0.dev/t/4AQFPprtCTI (template)
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"

import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { useState } from "react"
import { useTranslations } from "@/i18n/utils";
import type { Lang } from "@/i18n"

export default function Component(props: { lang?: Lang }) {
    const [wholesalePrice, setWholesalePrice] = useState(0)
    const [sellingPrice, setSellingPrice] = useState(0)
    const [shippingCost, setShippingCost] = useState(0)
    const [currency] = useState("EUR")
    const [vatRate, setVatRate] = useState(0.19)
    const [referralFeeRate, setReferralFeeRate] = useState(0.15)
    const [shippingFeeRate, setShippingFeeRate] = useState(0.15)

    const referralFee = sellingPrice * referralFeeRate
    const shippingFee = shippingCost * 0.15
    const vat = (sellingPrice + shippingCost) * vatRate / (1 + vatRate)
    const totalCost = wholesalePrice + shippingCost + referralFee + shippingFee + vat
    const profit = sellingPrice - totalCost
    const profitMargin = sellingPrice ? (profit / sellingPrice) * 100 : 0

    const getNumber = (value: string) => {
        const number = parseFloat(value)
        return isNaN(number) ? 0 : number
    }
    const t = useTranslations(props.lang)

    return (
        <Card className="mx-auto w-full max-w-xl">
            <CardHeader>
                <CardTitle>{t("calculator.title")}</CardTitle>
                <CardDescription>{t("calculator.description")}</CardDescription>
            </CardHeader>
            <CardContent className="gap-4 grid">
                <div className="gap-2 grid">
                    <Label htmlFor="sellingPrice">
                        <div className="flex items-center gap-2">
                            <ShoppingBagIcon className="w-4 h-4" />
                            <span>
                                {t("calculator.sellingPrice")}<br />
                                {t("calculator.sellingPrice.description")}
                            </span>
                        </div>
                    </Label>
                    <div className="flex items-center">
                        <Input
                            id="sellingPrice"
                            type="number"
                            min={0}
                            step="0.01"
                            value={sellingPrice}
                            onChange={(e) => setSellingPrice(getNumber(e.target.value))}
                            className="mr-2 w-full"
                        />
                    </div>
                </div>
                <div className="gap-2 grid grid-cols-2">
                    <div className="gap-2 grid">
                        <Label htmlFor="wholesalePrice">
                            <div className="flex items-center gap-2">
                                <PackageIcon className="w-4 h-4" />
                                {t("calculator.wholesalePrice")}
                            </div>
                        </Label>
                        <div className="flex items-center">
                            <Input
                                id="wholesalePrice"
                                type="number"
                                min={0}
                                step="0.01"
                                value={wholesalePrice}
                                onChange={(e) => setWholesalePrice(getNumber(e.target.value))}
                                className="w-full"
                            />
                        </div>
                    </div>
                    <div className="gap-2 grid">
                        <Label htmlFor="shippingCost">
                            <div className="flex items-center gap-2">
                                <TruckIcon className="w-4 h-4" />
                                {t("calculator.shippingCost")}
                            </div>
                        </Label>
                        <div className="flex items-center">
                            <Input
                                id="shippingCost"
                                type="number"
                                min={0}
                                step="0.01"
                                value={shippingCost}
                                onChange={(e) => setShippingCost(getNumber(e.target.value))}
                                className="w-full"
                            />
                        </div>
                    </div>
                    <div className="gap-2 grid">
                        <Label htmlFor="vatRate">
                            <div className="flex items-center gap-2">
                                <PercentIcon className="w-4 h-4" />
                                {t("calculator.vatRate")}
                            </div>
                        </Label>
                        <div className="flex items-center">
                            <Input
                                id="vatRate"
                                type="number"
                                min={0}
                                max={30}
                                step="1"
                                value={vatRate * 100}
                                onChange={(e) => setVatRate(getNumber(e.target.value) / 100)}
                                className="w-full"
                            />
                        </div>
                    </div>
                    <div className="gap-2 grid">
                        <Label htmlFor="referralFeeRate">
                            <div className="flex items-center gap-2">
                                <PercentIcon className="w-4 h-4" />
                                {t("calculator.referralFeeRate")}
                            </div>
                        </Label>
                        <div className="flex items-center">
                            <Input
                                id="referralFeeRate"
                                type="number"
                                min={0}
                                max={100}
                                step="1"
                                value={referralFeeRate * 100}
                                onChange={(e) => setReferralFeeRate(getNumber(e.target.value) / 100)}
                                className="w-full"
                            />
                        </div>
                    </div>
                    <div className="gap-2 grid">
                        <Label htmlFor="shippingFeeRate">
                            <div className="flex items-center gap-2">
                                <PercentIcon className="w-4 h-4" />
                                {t("calculator.shippingFeeRate")}
                            </div>
                        </Label>
                        <div className="flex items-center">
                            <Input
                                id="shippingFeeRate"
                                type="number"
                                min={0}
                                step="0.01"
                                value={shippingFeeRate * 100}
                                onChange={(e) => setShippingFeeRate(getNumber(e.target.value) / 100)}
                                className="w-full"
                            />
                        </div>
                    </div>
                </div>
            </CardContent>
            <CardContent className="gap-4 grid">
                <div className="flex items-center gap-2">
                    <PercentIcon className="w-4 h-4" />
                    <span>{t("calculator.referralFee")} ({referralFeeRate * 100}%)</span>
                    <span className="ml-auto font-semibold">
                        {currency} {referralFee.toFixed(2)}
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <PercentIcon className="w-4 h-4" />
                    <span>{t("calculator.shippingFee")} ({shippingFeeRate * 100}%)</span>
                    <span className="ml-auto font-semibold">
                        {currency} {shippingFee.toFixed(2)}
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <PercentIcon className="w-4 h-4" />
                    <span>{t("calculator.vat")} ({vatRate * 100}%)</span>
                    <span className="ml-auto font-semibold">
                        {currency} {vat.toFixed(2)}
                    </span>
                </div>
                <Separator />
                <div className="flex items-center gap-2">
                    <DollarSignIcon className="w-4 h-4" />
                    <span>{t("calculator.totalCost")}</span>
                    <span className="ml-auto font-semibold">
                        {currency} {totalCost.toFixed(2)}
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <DollarSignIcon className={`w-4 h-4 ${profit <= 0 ? 'text-red-500' : 'text-green-500'}`} />
                    <span className={`${profit <= 0 ? 'text-red-500' : 'text-green-500'}`}>{t("calculator.profit")}</span>
                    <span className={`ml-auto font-semibold ${profit <= 0 ? 'text-red-500' : 'text-green-500'}`}>
                        {currency} {profit.toFixed(2)}
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <PercentIcon className={`w-4 h-4 ${profit <= 0 ? 'text-red-500' : 'text-green-500'}`} />
                    <span className={`${profit <= 0 ? 'text-red-500' : 'text-green-500'}`}>{t("calculator.profitMargin")}</span>
                    <span className={`ml-auto font-semibold ${profit <= 0 ? 'text-red-500' : 'text-green-500'}`}>{profitMargin.toFixed(2)}%</span>
                </div>
            </CardContent>
        </Card>
    )
}


function DollarSignIcon(props: any) {
    return (
        <svg
            {...props}
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <line x1="12" x2="12" y1="2" y2="22" />
            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
        </svg>
    )
}


function PackageIcon(props: any) {
    return (
        <svg
            {...props}
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="m7.5 4.27 9 5.15" />
            <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
            <path d="m3.3 7 8.7 5 8.7-5" />
            <path d="M12 22V12" />
        </svg>
    )
}


function PercentIcon(props: any) {
    return (
        <svg
            {...props}
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <line x1="19" x2="5" y1="5" y2="19" />
            <circle cx="6.5" cy="6.5" r="2.5" />
            <circle cx="17.5" cy="17.5" r="2.5" />
        </svg>
    )
}


function ShoppingBagIcon(props: any) {
    return (
        <svg
            {...props}
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" />
            <path d="M3 6h18" />
            <path d="M16 10a4 4 0 0 1-8 0" />
        </svg>
    )
}


function TruckIcon(props: any) {
    return (
        <svg
            {...props}
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2" />
            <path d="M15 18H9" />
            <path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14" />
            <circle cx="17" cy="18" r="2" />
            <circle cx="7" cy="18" r="2" />
        </svg>
    )
}
